<template>
  <div class="page-container">
    <div ref="helpIndexPage" class="help-index-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 头部Banner -->
      <SearchBanner
        :title-main1="pageCfg.titleMain1"
        :title-main2="pageCfg.titleMain2"
        :title="pageCfg.title"
        :subtitle="pageCfg.subtitle"
        :placeholder="pageCfg.searchPlaceholder"
        :button-text="pageCfg.searchButton"
        :keyword.sync="searchKeyword"
        search-object="help"
        :background-image="bannerBg"
        :search-icon="searchIcon"
        @search="handleSearch"
      />

      <!-- 帮助文章列表 -->
      <div class="help-list-container">
        <div v-if="loading" class="loading-container">
          <div class="loading-text">{{ getText('common.loading') }}</div>
        </div>
        <HelpList 
          v-else
          :items="helpList"
          @item-click="handleItemClick"
        />

        <!-- 分页 -->
        <div class="pagination">
          <span class="pagination-info">{{ pageInfoText }}</span>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="goFirstPage">{{ pageCfg.pagination?.firstPage || '' }}</button>
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">{{ pageCfg.pagination?.prevPage || '' }}</button>
            <button
              v-for="page in visiblePages"
              :key="page"
              :class="['page-number', { active: currentPage === page }]"
              @click="goPage(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">{{ pageCfg.pagination?.nextPage || '' }}</button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="goLastPage">{{ pageCfg.pagination?.lastPage || '' }}</button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getHomeConfig } from '@/config/homeConfig'
import { getHelpConfig } from '@/config/helpConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import HelpList from '@/components/HelpList.vue'
import helpApi from '@/api/help'
import { getCurrentLocale, getText as i18nGetText } from '@/utils/i18nText'

export default {
  name: 'HelpIndex',
  components: {
    PageFooter,
    PageHeader,
    SearchBanner,
    Breadcrumb,
    HelpList
  },
  data() {
    return {
      config: {},
      helpConfig: {},
      searchKeyword: '',
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      helpList: [],
      loading: false,
      bannerBg: null,
      searchIcon: null,
      breadcrumbItems: []
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    pageCfg() {
      return this.helpConfig.help || {}
    },
    totalPages() {
      const total = Number(this.totalCount) || 0
      const size = Number(this.pageSize) || 20
      const pages = Math.ceil(total / size)
      return Number.isFinite(pages) && pages > 0 ? pages : 0
    },
    // 页码最多显示 6 个，采用滑动窗口
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (!total || total <= max) {
        return Array.from({ length: total || 1 }, (_, i) => i + 1)
      }
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },
    pageInfoText() {
      const tpl = this.pageCfg.pagination?.pageInfo || ''
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    }
  },
  created() {
    this.loadConfig()
    this.loadHelpList()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  watch: {
    searchKeyword() {
      // 搜索关键词变化时，重置到第一页并重新加载
      this.currentPage = 1
      this.loadHelpList()
    }
  },
  methods: {
    // 设置缩放比例
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$refs.helpIndexPage
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    getText(key) {
      return i18nGetText(key)
    },

    // 加载配置
    loadConfig() {
      const lang = getCurrentLocale()
      this.config = getHomeConfig(lang)
      this.helpConfig = getHelpConfig(lang)

      // 面包屑
      const breadcrumb = this.pageCfg.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.current || '', path: '' }
      ]

      // Banner 背景和搜索图标
      this.bannerBg = this.pageCfg.assets?.bannerBg || localeImg('help/head-bg.png')
      this.searchIcon = this.pageCfg.assets?.searchIcon || localeImg('help/search-ico.png')
    },

    // 加载帮助文章列表
    async loadHelpList() {
      this.loading = true
      try {
        const pageIndex = Math.max(0, (Number(this.currentPage) || 1) - 1)
        const params = {
          page: pageIndex,
          pageSize: Number(this.pageSize) || 20
        }
        
        const response = await helpApi.getHelpList(params)
        const result = response.data || {}
        
        if (result.status === 0 && result.data) {
          const data = result.data
          const list = Array.isArray(data.rows) ? data.rows : []
          
          // 转换数据格式
          this.helpList = list.map(item => {
            // 格式化日期（如果接口返回了时间字段）
            let dateText = ''
            if (item.ctime) {
              const date = new Date(item.ctime)
              const year = date.getFullYear()
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const day = String(date.getDate()).padStart(2, '0')
              dateText = `${year}-${month}-${day}`
            } else if (item.utime) {
              const date = new Date(item.utime)
              const year = date.getFullYear()
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const day = String(date.getDate()).padStart(2, '0')
              dateText = `${year}-${month}-${day}`
            } else {
              // 如果没有时间字段，使用当前日期
              const now = new Date()
              const year = now.getFullYear()
              const month = String(now.getMonth() + 1).padStart(2, '0')
              const day = String(now.getDate()).padStart(2, '0')
              dateText = `${year}-${month}-${day}`
            }
            
            return {
              id: item.id || '',
              title: item.title || '',
              date: dateText,
              content: item.content || '' // 保留内容，详情页可能需要
            }
          })
          
          // 更新总数（兼容 total / totalCount）
          this.totalCount = Number(data.totalCount ?? data.total ?? 0) || 0
          this.normalizeCurrentPage()
        } else {
          // 如果接口失败，使用模拟数据
          this.initMockData()
        }
      } catch (error) {
        console.error('加载帮助文章列表失败:', error)
        // 使用模拟数据
        this.initMockData()
      } finally {
        this.loading = false
      }
    },

    // 初始化模拟数据
    initMockData() {
      const baseTitles = [
        '账号注册常见问题',
        '需求发布常见问题',
        '成果发布常见问题',
        '技术经理人备案常见问题',
        '科技人才备案常见问题',
        '科技成果申请价值评估流程',
        '揭榜挂帅操作流程',
        '供需对接操作流程',
        '服务机构入驻常见问题',
        '服务商品发布常见问题',
        '个人用户系统操作手册',
        '交易中心使用说明',
        '科创服务申请流程',
        '企业用户注册指南',
        '如何发布技术需求',
        '如何发布科技成果',
        '技术经理人认证流程',
        '科技人才认证流程',
        '价值评估申请步骤',
        '揭榜挂帅项目发布指南',
        '供需信息匹配使用说明',
        '服务机构认证要求',
        '服务产品上架流程',
        '个人中心功能说明',
        '交易中心操作指南',
        '科创服务申请条件',
        '账号密码找回方法',
        '实名认证操作流程',
        '企业资质认证指南',
        '成果转化服务介绍',
        '技术转移服务说明',
        '知识产权服务指南',
        '创业孵化服务流程',
        '科技中介服务介绍',
        '检验检测服务说明',
        '金融服务机构介绍',
        '银行产品申请流程',
        '担保服务使用指南',
        '保险产品购买说明',
        '基金产品投资指南',
        '搜索功能使用说明',
        '筛选条件使用指南',
        '收藏功能操作说明',
        '消息通知设置方法',
        '数据导出功能说明',
        '报表查看操作指南',
        '权限管理说明',
        '数据安全保护措施',
        '隐私政策说明',
        '用户协议解读',
        '常见错误解决方案',
        '系统维护通知说明'
      ]

      // 生成更多 mock 数据（50条）
      const all = baseTitles.map((title, idx) => {
        // 生成不同的日期（最近3个月内的随机日期）
        const now = new Date()
        const daysAgo = Math.floor(Math.random() * 90) // 0-90天前
        const date = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const dateText = `${year}-${month}-${day}`
        
        return {
          id: String(idx + 1),
          title,
          date: dateText
        }
      })

      // mock 下也支持搜索过滤
      const keyword = (this.searchKeyword || '').trim()
      const filtered = keyword
        ? all.filter(item => String(item.title || '').includes(keyword))
        : all

      this.totalCount = filtered.length
      this.normalizeCurrentPage()

      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      this.helpList = filtered.slice(start, end)
    },

    normalizeCurrentPage() {
      const page = Number(this.currentPage)
      if (!Number.isFinite(page) || page < 1) {
        this.currentPage = 1
        return
      }
      if (this.totalPages > 0 && page > this.totalPages) {
        this.currentPage = this.totalPages
      }
    },

    // 搜索
    handleSearch(keyword) {
      this.searchKeyword = keyword
      this.currentPage = 1
      this.loadHelpList()
    },

    goPage(page) {
      const next = Number(page)
      if (!Number.isFinite(next) || next < 1 || next === this.currentPage) return
      this.currentPage = next
      this.loadHelpList()
    },

    // 上一页
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadHelpList()
      }
    },

    // 下一页
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadHelpList()
      }
    },

    goFirstPage() {
      if (this.currentPage === 1) return
      this.currentPage = 1
      this.loadHelpList()
    },

    goLastPage() {
      if (this.totalPages <= 0 || this.currentPage === this.totalPages) return
      this.currentPage = this.totalPages
      this.loadHelpList()
    },

    // 文章点击
    handleItemClick(item) {
      this.$router.push({
        name: 'helpDetail',
        params: { id: item.id },
        query: { 
          title: item.title,
          content: item.content || '' // 传递 content 数据
        }
      })
    },

    // 模块点击
    handleModuleClick(module) {
      this.$emit('module-click', module)
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.help-index-page {
  width: 1920px;
  min-height: 100vh;
  background: #ffffff;
  transform-origin: top left;
}

.help-list-container {
  width: 1700px;
  margin: 30px auto 50px;
  
  .loading-container {
    padding: 100px 0;
    text-align: center;
    
    .loading-text {
      font-family: Source Han Sans CN;
      font-size: 16px;
      color: #999999;
    }
  }
}

// 分页
.pagination {
  margin-top: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .pagination-info {
    font-family: Source Han Sans CN;
    font-weight: 400;
    font-size: 14px;
    color: #666666;
  }
  
  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    
    .page-btn {
      padding: 6px 16px;
      background: #FFFFFF;
      border: 1px solid rgba(160, 144, 60, 0.3);
      border-radius: 4px;
      font-family: Source Han Sans CN;
      font-weight: 400;
      font-size: 14px;
      color: #666666;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover:not(:disabled) {
        background: rgba(160, 144, 60, 0.1);
        border-color: rgba(160, 144, 60, 0.5);
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
    
    .page-number {
      min-width: 32px;
      height: 32px;
      padding: 0 8px;
      background: #FFFFFF;
      border: 1px solid rgba(160, 144, 60, 0.3);
      border-radius: 4px;
      font-family: Source Han Sans CN;
      font-weight: 400;
      font-size: 14px;
      color: #666666;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover {
        background: rgba(160, 144, 60, 0.1);
        border-color: rgba(160, 144, 60, 0.5);
      }
      
      &.active {
        background: #8C7C26;
        border-color: #8C7C26;
        color: #FFFFFF;
      }
    }
  }
}

</style>
